import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { PageContainer, PageHeader, Card, Button, FormGroup, Input, Select, Badge, Muted, EmptyState } from "../ui";

interface StoreTask {
  id: string;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  created_by_name: string;
  completed_by_name: string | null;
  completed_at: string | null;
  due_date: string | null;
  created_at: string;
}

const PRIORITY_BADGES: Record<string, { variant: "danger" | "gold" | "default" | "blue"; label: string }> = {
  urgent: { variant: "danger", label: "Urgent" },
  high: { variant: "gold", label: "High" },
  normal: { variant: "default", label: "Normal" },
  low: { variant: "blue", label: "Low" },
};

export default function StoreTasks() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";

  const [tasks, setTasks] = useState<StoreTask[]>([]);
  const [completedTasks, setCompletedTasks] = useState<StoreTask[]>([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState<Array<{ id: string; first_name: string; last_name: string }>>([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "normal",
    assigned_to: "",
    due_date: "",
  });

  useEffect(() => {
    loadTasks();
    if (isManager) {
      api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [isManager]);

  const loadTasks = async () => {
    const res = await api.get("/store-tasks");
    setTasks(res.data);
  };

  const loadCompleted = async () => {
    const res = await api.get("/store-tasks/completed");
    setCompletedTasks(res.data);
    setShowCompleted(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post("/store-tasks", {
      title: form.title,
      description: form.description || null,
      priority: form.priority,
      assigned_to: form.assigned_to || null,
      due_date: form.due_date || null,
    });
    setForm({ title: "", description: "", priority: "normal", assigned_to: "", due_date: "" });
    setShowForm(false);
    loadTasks();
  };

  const markDone = async (taskId: string) => {
    await api.put(`/store-tasks/${taskId}`, { status: "done" });
    loadTasks();
  };

  const markInProgress = async (taskId: string) => {
    await api.put(`/store-tasks/${taskId}`, { status: "in_progress" });
    loadTasks();
  };

  const deleteTask = async (taskId: string) => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/store-tasks/${taskId}`);
    loadTasks();
  };

  const openTasks = tasks.filter((t) => t.status === "open");
  const inProgressTasks = tasks.filter((t) => t.status === "in_progress");

  return (
    <PageContainer>
      <PageHeader
        title="Needs Done"
        subtitle={`${tasks.length} open task${tasks.length !== 1 ? "s" : ""}`}
        action={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => showCompleted ? setShowCompleted(false) : loadCompleted()}>
              {showCompleted ? "Open Tasks" : "Completed"}
            </Button>
            {isManager && (
              <Button onClick={() => setShowForm(!showForm)}>
                {showForm ? "Cancel" : "+ Add Task"}
              </Button>
            )}
          </div>
        }
      />

      {/* Add task form */}
      {showForm && (
        <Card className="mb-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <FormGroup label="What needs done?" span={2}>
                <Input
                  value={form.title}
                  onChange={(val) => setForm({ ...form, title: val })}
                  placeholder="e.g., Fix the cooler light in aisle 3"
                  required
                />
              </FormGroup>
              <FormGroup label="Details (optional)" span={2}>
                <Input
                  value={form.description}
                  onChange={(val) => setForm({ ...form, description: val })}
                  placeholder="Any extra info..."
                />
              </FormGroup>
              <FormGroup label="Priority">
                <Select value={form.priority} onChange={(val) => setForm({ ...form, priority: val })}>
                  <option value="low">Low - whenever</option>
                  <option value="normal">Normal</option>
                  <option value="high">High - soon</option>
                  <option value="urgent">Urgent - today</option>
                </Select>
              </FormGroup>
              <FormGroup label="Assign to (optional)">
                <Select value={form.assigned_to} onChange={(val) => setForm({ ...form, assigned_to: val })}>
                  <option value="">Anyone</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                  ))}
                </Select>
              </FormGroup>
            </div>
            <div className="flex gap-2">
              <Button type="submit">Add Task</Button>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Completed tasks view */}
      {showCompleted ? (
        <div className="space-y-2">
          {completedTasks.length === 0 ? (
            <EmptyState icon="&#9989;" message="No completed tasks yet" />
          ) : (
            completedTasks.map((task) => (
              <Card key={task.id} padding="px-5 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs" style={{ background: "#2D8A4E20", color: "var(--rbw-success)" }}>
                    &#10003;
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="line-through" style={{ color: "var(--rbw-gray-dark)" }}>{task.title}</span>
                  </div>
                  <div className="text-xs text-right flex-shrink-0">
                    <div><Muted>{task.completed_by_name}</Muted></div>
                    <div><Muted>{task.completed_at ? new Date(task.completed_at).toLocaleDateString() : ""}</Muted></div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      ) : (
        <>
          {/* In Progress */}
          {inProgressTasks.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: "var(--rbw-blue-dark)" }}>
                In Progress ({inProgressTasks.length})
              </h3>
              <div className="space-y-2">
                {inProgressTasks.map((task) => (
                  <TaskCard key={task.id} task={task} isManager={isManager} onDone={markDone} onDelete={deleteTask} />
                ))}
              </div>
            </div>
          )}

          {/* Open */}
          {openTasks.length > 0 ? (
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: "var(--rbw-gray-dark)" }}>
                To Do ({openTasks.length})
              </h3>
              <div className="space-y-2">
                {openTasks.map((task) => (
                  <TaskCard key={task.id} task={task} isManager={isManager} onDone={markDone} onStart={markInProgress} onDelete={deleteTask} />
                ))}
              </div>
            </div>
          ) : inProgressTasks.length === 0 ? (
            <EmptyState
              icon="&#127881;"
              message="All caught up!"
              detail="No tasks right now. Nice work."
            />
          ) : null}
        </>
      )}
    </PageContainer>
  );
}

function TaskCard({
  task,
  isManager,
  onDone,
  onStart,
  onDelete,
}: {
  task: StoreTask;
  isManager: boolean;
  onDone: (id: string) => void;
  onStart?: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const priority = PRIORITY_BADGES[task.priority] || PRIORITY_BADGES.normal;
  const isInProgress = task.status === "in_progress";

  return (
    <Card padding="px-5 py-4">
      <div className="flex items-start gap-4">
        {/* Done button */}
        <button
          onClick={() => onDone(task.id)}
          className="w-7 h-7 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center transition-all"
          style={{
            border: `2px solid ${isInProgress ? "var(--rbw-blue)" : "var(--rbw-border)"}`,
            background: isInProgress ? "#8BB8E810" : "transparent",
          }}
          title="Mark done"
        >
          <span className="text-xs opacity-0 hover:opacity-100 transition-opacity" style={{ color: "var(--rbw-success)" }}>&#10003;</span>
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-semibold" style={{ color: "var(--rbw-navy)" }}>{task.title}</span>
            <Badge variant={priority.variant}>{priority.label}</Badge>
            {isInProgress && <Badge variant="blue">Working on it</Badge>}
          </div>
          {task.description && (
            <div className="text-sm mt-1"><Muted>{task.description}</Muted></div>
          )}
          <div className="flex gap-4 mt-2 text-xs">
            <Muted>Added by {task.created_by_name}</Muted>
            {task.assigned_to_name && <Muted>Assigned to {task.assigned_to_name}</Muted>}
            {task.due_date && (
              <span style={{
                color: new Date(task.due_date) < new Date() ? "var(--rbw-danger)" : "var(--rbw-gray-dark)"
              }}>
                Due {new Date(task.due_date).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-1 flex-shrink-0">
          {onStart && !isInProgress && (
            <button
              onClick={() => onStart(task.id)}
              className="px-2 py-1 rounded text-xs font-medium transition-all"
              style={{ color: "var(--rbw-blue-dark)" }}
              title="Start working on this"
            >
              Start
            </button>
          )}
          {isManager && (
            <button
              onClick={() => onDelete(task.id)}
              className="px-2 py-1 rounded text-xs font-medium transition-all"
              style={{ color: "var(--rbw-gray-dark)" }}
              title="Delete"
            >
              &times;
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}
